package com.yendo.mx.ui.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Devices
import androidx.compose.material.icons.filled.PhonelinkLock
import androidx.compose.material.icons.filled.Smartphone
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.models.DispositivoVinculado
import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.utils.BackHandler
import com.yendo.mx.utils.obtenerIdDispositivo
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DispositivosScreen(onBack: () -> Unit) {
    val usuario by AuthRepository.usuarioActual.collectAsState()
    val listaDispositivos = usuario?.infoDispositivos ?: emptyList()
    val idActual = remember { obtenerIdDispositivo() }

    BackHandler { onBack() }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Dispositivos", fontWeight = FontWeight.ExtraBold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Atrás")
                    }
                }
            )
        }
    ) { padding ->
        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(padding).padding(horizontal = 20.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            item {
                Spacer(Modifier.height(8.dp))
                Text(
                    "Estos son los dispositivos que tienen acceso a tu cuenta de Yendo MX.",
                    color = Color.Gray,
                    fontSize = 14.sp
                )
                Spacer(Modifier.height(8.dp))
            }

            items(listaDispositivos) { dispositivo ->
                CardDispositivo(
                    dispositivo = dispositivo,
                    esActual = dispositivo.id == idActual
                )
            }

            item {
                Spacer(Modifier.height(24.dp))
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(12.dp))
                        .background(RosaYendo.copy(alpha = 0.05f))
                        .padding(16.dp)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.PhonelinkLock, null, tint = RosaYendo)
                        Spacer(Modifier.width(12.dp))
                        Text(
                            "Si ves un dispositivo que no reconoces, te recomendamos cambiar tu contraseña o contactar a soporte.",
                            fontSize = 12.sp,
                            color = RosaYendo,
                            lineHeight = 18.sp
                        )
                    }
                }
                Spacer(Modifier.height(40.dp))
            }
        }
    }
}

@Composable
fun CardDispositivo(dispositivo: DispositivoVinculado, esActual: Boolean) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (esActual) RosaYendo.copy(alpha = 0.08f) else Color.White
        ),
        border = if (esActual) androidx.compose.foundation.BorderStroke(1.dp, RosaYendo.copy(alpha = 0.5f)) else null
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(CircleShape)
                    .background(if (esActual) RosaYendo else Color(0xFFF0F0F0)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    if (dispositivo.sistema.contains("iOS", true)) Icons.Default.Smartphone else Icons.Default.Devices,
                    null,
                    tint = if (esActual) Color.White else Color.Gray
                )
            }

            Spacer(Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = dispositivo.nombre,
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp
                    )
                    if (esActual) {
                        Spacer(Modifier.width(8.dp))
                        Surface(
                            color = RosaYendo,
                            shape = RoundedCornerShape(4.dp)
                        ) {
                            Text(
                                "ESTE EQUIPO",
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                fontSize = 9.sp,
                                color = Color.White,
                                fontWeight = FontWeight.Black
                            )
                        }
                    }
                }
                Text(
                    text = "${dispositivo.modelo} • ${dispositivo.sistema}",
                    color = Color.Gray,
                    fontSize = 13.sp
                )
                Text(
                    text = "Última conexión: ${formatearFechaConexion(dispositivo.ultimaConexion)}",
                    color = Color.Gray,
                    fontSize = 11.sp
                )
            }
        }
    }
}

fun formatearFechaConexion(timestamp: Long): String {
    if (timestamp == 0L) return "Desconocida"
    return try {
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
        val dia = dateTime.dayOfMonth.toString().padStart(2, '0')
        val mes = dateTime.monthNumber.toString().padStart(2, '0')
        val anio = dateTime.year
        val hora = dateTime.hour.toString().padStart(2, '0')
        val min = dateTime.minute.toString().padStart(2, '0')
        "$dia/$mes/$anio a las $hora:$min"
    } catch (_: Exception) {
        "Reciente"
    }
}
